<template>
  <GcBaseLayout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :tabs="tabs"
    :selectedIndex="selectedIndex"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        title="Customer"
        :baseView="baseView"
        :primaryLabel="model.CustomerName"
        :secondaryLabel="baseView.id"
        recordType="Customer"
        v-if="loaded"
      />
    </template>

    <div class="css-v2 transaction-list customer-price-groups">
      <GcPopupPriceGroupSelect
        :setVisible="showAddModal"
        :popupHiddenCallBack="closeAddModal"
        :assignedPriceGroups="assignedPriceGroups"
        :customerCode="model.CustomerCode"
        :onSaveSuccess="onSaveSuccess"
        :displayServerError="displayServerError"
        :showLoading="showLoading"
        v-if="showAddModal"
      />
      <div class="data-grid-details">
        <GcDataGrid
          :dataSourceConfig="gridDataSource"
          :columns="gridColumns"
          :gridToolbar="gridToolbar"
          :padColumn="true"
          :id="'price-group-grid'"
          v-if="loaded"
          ref="priceGroupGrid"
          :isEditableGrid="true"
          :showFooter="false"
          :gridOptions="gridOptions"
          :elementAttrClass="'transaction-template'"
          :paging="true"
          :heightPadding="20"
          :key="gridKey"
          :isLocalDataSource="true"
        />
      </div>
    </div>
  </GcBaseLayout>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import CustomerTabsMixin from "@/_mixins/customer-tabs.mixin";
import { CustomerService } from "../../_services/api/customers/customer.service";
import { CustomerPriceGroupService } from "../../_services/api/customers/customer-price-group.service";
import { FeatureStorage } from "../../_services/storage";
import ColumnWidth from "../../_helpers/data-grid/ColumnWidth";
import GcPopupPriceGroupSelect from "../../popups/customers/gc-popup-price-group-select.vue";

export default {
  mixins: [RouteMixin, FormMixin, CustomerTabsMixin],
  components: { GcPopupPriceGroupSelect },
  data() {
    return {
      backButtonRoute: `/customer`,
      selectedIndex: 0,
      loaded: false,
      isDirty: false,
      gridKey: 0,
      model: {
        CustomerCode: "",
        CustomerName: "",
      },
      assignedPriceGroups: [],
      showAddModal: false,
    };
  },
  computed: {
    visiblePriceGroups() {
      return this.assignedPriceGroups.filter((pg) => pg.Include !== false);
    },
    gridDataSource() {
      return {
        localDataList: true,
        items: this.visiblePriceGroups,
        allowGrouping: false,
      };
    },
    gridOptions() {
      return {
        noDataMessage: {
          addNew: {
            class: "no-data",
            title: "No Price Groups",
            message: "No price groups have been assigned to this customer.",
          },
        },
        options: {
          selectionMode: "multiple",
        },
      };
    },
    gridToolbar() {
      return {
        search: false,
        columnChooser: true,
        export: false,
        openFilter: false,
        expand: false,
        customButtons: [
          {
            icon: "plus",
            label: "Add New",
            buttonLocation: "left",
            visible: FeatureStorage.checkPermission("PriceGroup", "create"),
            clickAction: () => this.openAddModal(),
          },

          {
            icon: "more",
            widget: "dxDropDownButton",
            label: "",
            clickAction: {},
            buttonLocation: "right",
            options: {
              icon: "more",
              showArrowIcon: false,
              keyExpr: "menuText",
              displayExpr: "menuText",
              dropDownOptions: { width: 150 },
              items: [
                {
                  menuText: "Remove",
                },
              ],
              onItemClick: () => {
                this.bulkRemove();
              },
            },
          },
        ],
      };
    },
    gridColumns() {
      return [
        {
          dataField: "PriceGroupID",
          label: "Price Group",
          width: 100,
          alignment: "start",
          allowHiding: false,
        },
        {
          dataField: "ShortDescription",
          label: "Short Description",
          allowHiding: false,
        },
        {
          dataField: "Description",
          label: "Description",
          width: ColumnWidth.Description.width,
          minWidth: ColumnWidth.Description.minWidth,
        },
        {
          screenField: "DateIn",
          dataField: "StartDate",
          label: "Start Date",
          dataType: "date",
          format: "dd/MM/yyyy",
          width: ColumnWidth.Date.width,
          minWidth: ColumnWidth.Date.minWidth,
        },
        {
          screenField: "DateIn",
          dataField: "EndDate",
          label: "End Date",
          dataType: "date",
          format: "dd/MM/yyyy",
          width: ColumnWidth.Date.width,
          minWidth: ColumnWidth.Date.minWidth,
        },
        {
          dataField: "Include",
          label: "Included",
          width: ColumnWidth.Included.width,
          minWidth: ColumnWidth.Included.minWidth,
          cssClass: "include-column",
        },
        {
          dataField: "InheritedMessage",
          label: "Inherited",
          width: ColumnWidth.Comment.width,
          minWidth: ColumnWidth.Comment.minWidth,
        },
        {
          screenField: "GridRecordEllipsisMenu",
          cssClass: "padding-7",
          width: 50,
          allowSearch: false,
          ellipsisMenu: {
            itemAction: (data) => {
              var items = [
                {
                  menuText: "Remove",
                  data: data,
                  onClick: () => this.removeRow(data.data),
                },
              ];
              return items;
            },
            keyExpr: "menuText",
            displayExpr: "menuText",
          },
          makeDisabled: (data) => {
            if (!data) return true;
            if (data.data.InheritedMessage) return true;
          },
        },
      ];
    },
  },
  watch: {
    "visiblePriceGroups.length": function () {
      this.gridKey += 1;
    },
  },
  async created() {
    this.showLoading(true);
    this.selectedIndex = this.tabs.findIndex((x) => x.text === "Price Groups");

    await CustomerService.getByCode(this.baseView.id, ["Code", "Name"]).then(
      (response) => {
        if (response.data.value.length > 0) {
          this.model.CustomerCode = response.data.value[0].Code;
          this.model.CustomerName = response.data.value[0].Name;
          this.baseView.tempId = this.model.CustomerCode;
        }
      },
    );

    this.loaded = true;
    await this.loadAssignedPriceGroups();

    this.$nextTick(() => {
      this.showLoading(false);
    });
  },
  methods: {
    /**
     * Navigates back to the customer details page when the back button is clicked.
     */
    backButtonFunction() {
      this.$router.push({
        path: this.backButtonRoute,
      });
    },
    /**
     * Loads the price groups assigned to the customer and updates the grid.
     */
    async loadAssignedPriceGroups() {
      await CustomerPriceGroupService.getByCustomerCode(this.baseView.id).then(
        (response) => {
          this.assignedPriceGroups = response.data.value || [];
          this.gridKey += 1;
        },
      );
    },
    /**
     * Opens the modal to add a new price group to the customer.
     */
    openAddModal() {
      this.showAddModal = true;
    },
    /**
     * Closes the add price group modal.
     */
    closeAddModal() {
      this.showAddModal = false;
    },
    /**
     * Handles the success callback after saving price group changes, reloads the assigned price groups and resizes the grid.
     */
    async onSaveSuccess() {
      await this.loadAssignedPriceGroups();

      this.$nextTick(() => {
        this.$refs["priceGroupGrid"].reSizeGrid();
      });
    },
    /**
     * Handles the bulk removal of price groups from the customer, including confirmation and error handling.
     */
    async bulkRemove() {
      const selectedRows = this.$refs["priceGroupGrid"].getSelectedRowsData();
      if (selectedRows.length === 0) {
        this.$alert(
          "Please select at least one price group to remove.",
          "No Selection",
        );
        return;
      }

      const isAnyInherited = selectedRows.some((row) => row.InheritedMessage);
      if (isAnyInherited) {
        this.$alert(
          "One or more selected price groups are inherited and cannot be removed.",
          "Invalid Selection",
        );
        return;
      }

      const confirmRemove = await this.$confirm(
        `Are you sure you want to remove ${selectedRows.length} Price Group(s) from this customer?`,
        "Confirm Removal",
      );

      if (confirmRemove) {
        const payload = {
          CustomerCode: selectedRows[0].CustomerCode,
          PriceGroups: selectedRows.map((row) => ({
            PriceGroupID: row.PriceGroupID,
            CustomerCode: row.CustomerCode,
            Include: false,
            InheritedMessage: row.InheritedMessage,
          })),
        };

        this.removePriceGroups(payload, "bulk");
      }
    },
    /**
     * Handles the removal of a single price group from the customer, including confirmation and error handling.
     */
    async removeRow(row) {
      const confirmDelete = await this.$confirm(
        `Are you sure you want to remove Price Group ${row.PriceGroupID} from this customer?`,
        "Confirm Removal",
      );

      if (confirmDelete) {
        const payload = {
          CustomerCode: row.CustomerCode,
          PriceGroups: [
            {
              PriceGroupID: row.PriceGroupID,
              CustomerCode: row.CustomerCode,
              Include: false,
              InheritedMessage: row.InheritedMessage,
            },
          ],
        };

        this.removePriceGroups(payload, "single");
      }
    },
    /**
     * Sends a request to remove price groups from the customer and handles the response, including success notifications and error handling.
     * @param {Object} payload - The payload containing the customer code and price groups to be removed.
     * @param {string} type - The type of removal operation, either "single" or "bulk", used to determine the success notification message.
     * @returns {Promise<void>}
     */
    async removePriceGroups(payload, type) {
      this.showLoading(true);
      await CustomerPriceGroupService.save(payload)
        .then(() => {
          if (type === "single") {
            this.$notify(
              `Price Group ${payload.PriceGroups[0].PriceGroupID} has been successfully removed.`,
              "success",
            );
          } else if (type === "bulk") {
            this.$notify(
              `${payload.PriceGroups.length} Price Group(s) have been successfully removed.`,
              "success",
            );
          }
          this.onSaveSuccess();
        })
        .catch((error) => {
          this.showLoading(false);
          if (type === "bulk") {
            this.displayServerError(
              error,
              `An error occurred while removing the selected Price Groups. Please try again.`,
            );
            return;
          }
          this.displayServerError(
            error,
            `An error occurred while removing Price Group ${payload.PriceGroups[0].PriceGroupID}. Please try again.`,
          );
        })
        .finally(() => {
          this.showLoading(false);
        });
    },
  },
};
</script>

<style lang="scss">
.customer-price-groups {
  .include-column::before {
    content: unset !important;
  }
}
</style>
